import { create } from 'zustand'
import { apiErrorMessage } from '../api/client'

export type RunRecord = {
  id: string
  status: string
  tenantId?: string
  agentId?: string
  agentVersionId?: string
  startedAt?: string
  finishedAt?: string
  queuedAt?: string
  errorCode?: string
  errorMessage?: string
  inputPayload?: Record<string, unknown>
  abTestGroup?: string
  abTestId?: string
}

export type StepRecord = {
  id: string
  runId: string
  stepIndex: number
  stepType: string
  status: string
  startedAt?: string
  finishedAt?: string
  inputPayload?: Record<string, unknown>
  outputPayload?: Record<string, unknown>
  errorCode?: string
  errorMessage?: string
  feedback?: number
  feedbackNote?: string
}

export type TraceRecord = {
  id: string
  runId: string
  stepId?: string
  status: string
  startedAt: string
  finishedAt?: string
  executionMs?: number
  stdout?: string
  stderr?: string
  errorCode?: string
  errorMessage?: string
  skillId?: string
  workerRef?: string
  metadata?: Record<string, unknown>
}

export type UsageRecord = {
  runId: string
  stepId?: string
  meterCode: string
  quantity: number
  unit: string
  provider?: string
  costMicros?: number
  occurredAt?: string
  metadata?: Record<string, unknown>
}

// KnownEnvelopeType is the set of event types the store handles explicitly.
type KnownEnvelopeType = 'run.updated' | 'step.created' | 'step.updated' | 'trace.recorded' | 'usage.recorded'

export type StreamEnvelope =
  | { type: 'run.updated';    payload: RunRecord    }
  | { type: 'step.created';   payload: StepRecord   }
  | { type: 'step.updated';   payload: StepRecord   }
  | { type: 'trace.recorded'; payload: TraceRecord  }
  | { type: 'usage.recorded'; payload: UsageRecord  }
  // Forward-compatibility catch-all: unknown future events are passed through.
  | { type: Exclude<string, KnownEnvelopeType>; payload: unknown }

export type UsageTotals = {
  totalTokens: number
  inputTokens: number
  outputTokens: number
  costMicros: number
  executionMs?: number
  provider?: string
}

export type UsageProviderSummary = {
  provider: string
  totalTokens: number
  inputTokens: number
  outputTokens: number
  executionMs: number
  costMicros: number
}

export type ApprovalRecord = {
  id: string
  runId: string
  stepId?: string
  skillId?: string
  status: string
  payload?: Record<string, unknown>
  editedPayload?: Record<string, unknown>
  requestedAt: string
  decidedAt?: string
  decidedByUserID?: string
  expiresAt?: string
}

export type ObservabilityHydrationResponse = {
  run: RunRecord
  steps: Record<string, StepRecord>
  tracesByStep: Record<string, TraceRecord[]>
  usageByProvider: UsageProviderSummary[]
  usageTotals: UsageProviderSummary
  approvals: Record<string, ApprovalRecord>
  layoutVersion: number
}

// ── History-list types ────────────────────────────────────────────────────────

export type HistoryFetchOpts = {
  /** Cursor from the previous page (omit for first page). */
  cursor?: string
  /** Filter by run status (e.g. "succeeded", "failed"). */
  status?: string
  /** Append results to the existing list instead of replacing them. */
  append?: boolean
  /** Optional fetch init (auth headers etc.). */
  init?: RequestInit
}

// ── Main store shape ──────────────────────────────────────────────────────────

export type ObservabilityState = {
  // ── Live / historical run view ──────────────────────────────────────────────
  run?: RunRecord
  steps: Record<string, StepRecord>
  tracesByStep: Record<string, TraceRecord[]>
  usageByStep: Record<string, UsageRecord[]>
  usageTotals: UsageTotals
  usageByProvider: UsageProviderSummary[]
  approvals: Record<string, ApprovalRecord>
  layoutVersion: number
  selectedStepId?: string
  /** ID of the DAG node currently open in the ReviewSidebar (null = closed). */
  selectedApprovalNodeId: string | null
  /**
   * True when the user navigated to a completed run from the history sidebar.
   * The ReviewSidebar hides Approve/Reject and shows the recorded decision instead.
   */
  isReadOnly: boolean

  // ── Run history list (cached across sidebar open/close) ─────────────────────
  historyRuns: RunRecord[]
  /** Cursor for the next history page, null when all pages are loaded. */
  historyNext: string | null
  historyLoading: boolean
  historyError: string | null

  // ── Actions ─────────────────────────────────────────────────────────────────
  applyBatch: (batch: StreamEnvelope[]) => void
  hydrate: (runId: string, init?: RequestInit) => Promise<void>
  selectStep: (stepId?: string) => void
  /** Open or close the ReviewSidebar for a specific approval node. */
  setSelectedApprovalNode: (id: string | null) => void
  /** Flip the read-only flag (called when the user selects a run from history). */
  setIsReadOnly: (v: boolean) => void
  /** Fetch the run history list.  Pass append:true for infinite scroll. */
  fetchHistory: (opts?: HistoryFetchOpts) => Promise<void>
}

const EMPTY_TOTALS: UsageTotals = {
  totalTokens: 0,
  inputTokens: 0,
  outputTokens: 0,
  costMicros: 0,
}

function deriveTokenTotals(record: UsageRecord): Partial<UsageTotals> {
  const quantity = Number(record.quantity || 0)
  if (record.unit !== 'tokens') {
    return {
      costMicros: Number(record.costMicros || 0),
    }
  }

  const meter = record.meterCode.toLowerCase()
  if (meter.includes('input')) {
    return {
      totalTokens: quantity,
      inputTokens: quantity,
      costMicros: Number(record.costMicros || 0),
    }
  }
  if (meter.includes('output')) {
    return {
      totalTokens: quantity,
      outputTokens: quantity,
      costMicros: Number(record.costMicros || 0),
    }
  }
  return {
    totalTokens: quantity,
    costMicros: Number(record.costMicros || 0),
  }
}

function mergeUsageTotals(current: UsageTotals, record: UsageRecord): UsageTotals {
  const delta = deriveTokenTotals(record)
  return {
    totalTokens: current.totalTokens + (delta.totalTokens || 0),
    inputTokens: current.inputTokens + (delta.inputTokens || 0),
    outputTokens: current.outputTokens + (delta.outputTokens || 0),
    costMicros: current.costMicros + (delta.costMicros || 0),
    executionMs: current.executionMs,
    provider: current.provider || 'all',
  }
}

function mergeProviderSummaries(current: UsageProviderSummary[], record: UsageRecord): UsageProviderSummary[] {
  const provider = record.provider || 'platform'
  const next = [...current]
  const index = next.findIndex((item) => item.provider === provider)
  const existing =
    index >= 0
      ? next[index]
      : {
          provider,
          totalTokens: 0,
          inputTokens: 0,
          outputTokens: 0,
          executionMs: 0,
          costMicros: 0,
        }

  const updated: UsageProviderSummary = {
    ...existing,
    costMicros: existing.costMicros + Number(record.costMicros || 0),
  }

  if (record.unit === 'tokens') {
    updated.totalTokens += Number(record.quantity || 0)
    const meter = record.meterCode.toLowerCase()
    if (meter.includes('input')) {
      updated.inputTokens += Number(record.quantity || 0)
    } else if (meter.includes('output')) {
      updated.outputTokens += Number(record.quantity || 0)
    }
  }
  if (record.meterCode === 'worker.step.execution_ms') {
    updated.executionMs += Number(record.quantity || 0)
  }

  if (index >= 0) {
    next[index] = updated
    return next
  }
  next.push(updated)
  return next.sort((a, b) => b.costMicros - a.costMicros)
}

function appendTrace(existing: TraceRecord[] | undefined, next: TraceRecord): TraceRecord[] {
  const items = existing ? [...existing] : []
  const idx = items.findIndex((item) => item.id === next.id)
  if (idx >= 0) {
    items[idx] = next
    return items
  }
  items.push(next)
  items.sort((a, b) => a.startedAt.localeCompare(b.startedAt))
  return items
}

function appendUsage(existing: UsageRecord[] | undefined, next: UsageRecord): UsageRecord[] {
  return [...(existing || []), next]
}

export const useObservabilityStore = create<ObservabilityState>((set) => ({
  steps: {},
  tracesByStep: {},
  usageByStep: {},
  usageTotals: EMPTY_TOTALS,
  usageByProvider: [],
  approvals: {},
  layoutVersion: 0,
  selectedApprovalNodeId: null,
  isReadOnly: false,
  historyRuns: [],
  historyNext: null,
  historyLoading: false,
  historyError: null,
  setSelectedApprovalNode: (id) => set({ selectedApprovalNodeId: id }),
  setIsReadOnly: (v) => set({ isReadOnly: v }),
  fetchHistory: async (opts = {}) => {
    const { cursor, status, append = false, init } = opts
    set({ historyLoading: true, historyError: null })
    try {
      const params = new URLSearchParams({ limit: '25' })
      if (cursor)  params.set('cursor', cursor)
      if (status)  params.set('status', status)
      const res = await fetch(`/v1/runs?${params.toString()}`, {
        method: 'GET',
        ...init,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      // Normalise: the real backend sends { items, page: { nextCursor } }
      // the mock server sends { runs, total, page (number), pageSize }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = (await res.json()) as Record<string, any>
      const items: RunRecord[] = Array.isArray(raw)
        ? raw
        : (raw['items'] ?? raw['runs'] ?? raw['data'] ?? []) as RunRecord[]
      const nextCursor: string | null =
        typeof raw['page'] === 'object' && raw['page'] !== null
          ? (raw['page']['nextCursor'] ?? null)
          : (raw['nextCursor'] ?? null)
      set((state) => ({
        historyRuns:    append ? [...(state.historyRuns ?? []), ...items] : items,
        historyNext:    nextCursor,
        historyLoading: false,
      }))
    } catch (e) {
      set({ historyLoading: false, historyError: apiErrorMessage(e, 'Failed to load run history') })
    }
  },
  applyBatch: (batch) =>
    set((state) => {
      const next: ObservabilityState = {
        ...state,
        steps: { ...state.steps },
        tracesByStep: { ...state.tracesByStep },
        usageByStep: { ...state.usageByStep },
        usageTotals: { ...state.usageTotals },
        usageByProvider: [...state.usageByProvider],
        approvals: { ...state.approvals },
      }

      for (const envelope of batch) {
        // TypeScript cannot narrow the catch-all union member via switch/case,
        // so we cast explicitly in each branch. The runtime type is guaranteed
        // by the server — these casts are safe.
        switch (envelope.type) {
          case 'run.updated': {
            const p = envelope.payload as RunRecord
            next.run = p
            break
          }
          case 'step.created': {
            const p = envelope.payload as StepRecord
            const prev = next.steps[p.id]
            next.steps[p.id] = p
            if (!prev) {
              next.layoutVersion += 1
            }
            break
          }
          case 'step.updated': {
            const p = envelope.payload as StepRecord
            next.steps[p.id] = {
              ...(next.steps[p.id] || {}),
              ...p,
            }
            break
          }
          case 'trace.recorded': {
            const p = envelope.payload as TraceRecord
            const key = p.stepId || '__run__'
            next.tracesByStep[key] = appendTrace(next.tracesByStep[key], p)
            break
          }
          case 'usage.recorded': {
            const p = envelope.payload as UsageRecord
            const key = p.stepId || '__run__'
            next.usageByStep[key] = appendUsage(next.usageByStep[key], p)
            next.usageTotals = mergeUsageTotals(next.usageTotals, p)
            next.usageByProvider = mergeProviderSummaries(next.usageByProvider, p)
            break
          }
          default:
            break
        }
      }

      return next
    }),
  hydrate: async (runId, init) => {
    const response = await fetch(`/v1/runs/${encodeURIComponent(runId)}/observability`, {
      method: 'GET',
      ...init,
    })
    if (!response.ok) {
      throw new Error(`Failed to hydrate observability for run ${runId}`)
    }
    const payload = (await response.json()) as ObservabilityHydrationResponse
    set({
      run: payload.run,
      steps: payload.steps || {},
      tracesByStep: payload.tracesByStep || {},
      usageByProvider: payload.usageByProvider || [],
      usageTotals: {
        totalTokens: payload.usageTotals?.totalTokens || 0,
        inputTokens: payload.usageTotals?.inputTokens || 0,
        outputTokens: payload.usageTotals?.outputTokens || 0,
        costMicros: payload.usageTotals?.costMicros || 0,
        executionMs: payload.usageTotals?.executionMs || 0,
        provider: payload.usageTotals?.provider || 'all',
      },
      approvals: payload.approvals || {},
      layoutVersion: payload.layoutVersion || Object.keys(payload.steps || {}).length,
      usageByStep: {},
    })
  },
  selectStep: (stepId) => set({ selectedStepId: stepId }),
}))

const pendingEnvelopes: StreamEnvelope[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

function flushPendingEnvelopes() {
  flushTimer = null
  if (pendingEnvelopes.length === 0) {
    return
  }
  const batch = pendingEnvelopes.splice(0, pendingEnvelopes.length)
  useObservabilityStore.getState().applyBatch(batch)
}

export function enqueueObservabilityEnvelope(envelope: StreamEnvelope) {
  pendingEnvelopes.push(envelope)
  if (flushTimer !== null) {
    return
  }
  flushTimer = setTimeout(flushPendingEnvelopes, 100)
}

export function enqueueObservabilityBatch(batch: StreamEnvelope[]) {
  pendingEnvelopes.push(...batch)
  if (flushTimer !== null) {
    return
  }
  flushTimer = setTimeout(flushPendingEnvelopes, 100)
}
